/**
 * JUICE · Klaviyo subscribe.
 *
 * Every email capture on the site (popup, footer, homepage band) posts here rather
 * than to Shopify's customer form, so subscribers land in Klaviyo where the flows
 * live. Progressive enhancement: the markup is a real <form>, so if this script
 * fails to load the field still submits somewhere sane rather than doing nothing.
 *
 * Uses Klaviyo's CLIENT endpoint, which is CORS-enabled and takes the PUBLIC site ID
 * (company_id) — the same value already in the page source. No private key is
 * involved and nothing secret ships to the browser.
 *
 * Markup contract:
 *   <form data-klaviyo-subscribe
 *         data-klaviyo-company="XXXXXX"
 *         data-klaviyo-list="XXXXXX"
 *         data-klaviyo-source="popup|footer|newsletter">
 *     <input type="email" name="email" required>
 *     <button type="submit">…</button>
 *     <p data-klaviyo-message hidden></p>
 *   </form>
 */
(() => {
  // The asset is loaded once from layout/theme.liquid; this guard just means an extra
  // include can never bind a second handler and fire the subscribe twice.
  if (window.__juiceKlaviyoBound) return;
  window.__juiceKlaviyoBound = true;

  const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const REVISION = '2024-10-15';

  const setMessage = (form, text, kind) => {
    const el = form.querySelector('[data-klaviyo-message]');
    if (!el) return;
    el.textContent = text;
    el.hidden = !text;
    el.dataset.state = kind || '';
  };

  const subscribe = async (form) => {
    const company = form.dataset.klaviyoCompany;
    const list = form.dataset.klaviyoList;
    const input = form.querySelector('input[type="email"]');
    const button = form.querySelector('[type="submit"]');
    const email = (input?.value || '').trim();

    if (!company || !list) {
      // Misconfigured rather than user error — say so plainly instead of failing mute.
      setMessage(form, 'Sign-up is not configured yet. Please try again later.', 'error');
      return;
    }
    if (!email) return;

    if (button) button.disabled = true;
    setMessage(form, '', '');

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties: { signup_source: form.dataset.klaviyoSource || 'website' },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: list } } },
      },
    };

    try {
      const res = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(company)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: REVISION },
        body: JSON.stringify(payload),
      });

      // Klaviyo answers 202 Accepted with an empty body on success.
      if (res.status === 202 || res.ok) {
        form.dataset.klaviyoDone = 'true';
        setMessage(form, form.dataset.klaviyoSuccess || "You're on the list. Check your inbox.", 'success');
        if (input) input.value = '';
        form.dispatchEvent(new CustomEvent('juice:subscribed', { bubbles: true, detail: { email } }));
      } else {
        setMessage(form, form.dataset.klaviyoError || "That didn't go through. Please try again.", 'error');
      }
    } catch (e) {
      setMessage(form, form.dataset.klaviyoError || "That didn't go through. Please try again.", 'error');
    } finally {
      if (button) button.disabled = false;
    }
  };

  // Delegated, so forms rendered later (the popup mounts on a timer) are covered.
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-klaviyo-subscribe]');
    if (!form) return;
    event.preventDefault();
    subscribe(form);
  });
})();
